import React, { useState, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, PieChart, Radar } from 'lucide-react';
import { TestResults } from '../../types';

// Lazy load chart components
const ResultsChart = lazy(() => import('./ResultsChart'));
const ResultsPieChart = lazy(() => import('./ResultsPieChart'));
const ResultsBarChart = lazy(() => import('./ResultsBarChart'));

interface ResultsVisualizationProps {
  results: TestResults;
}

type ChartType = 'radar' | 'pie' | 'bar';

const ResultsVisualization: React.FC<ResultsVisualizationProps> = ({ results }) => {
  const [activeChart, setActiveChart] = useState<ChartType>('radar');

  const chartTabs = [
    { id: 'radar' as ChartType, label: 'Radar Chart', icon: Radar, description: 'Overall readiness overview' },
    { id: 'pie' as ChartType, label: 'Category Distribution', icon: PieChart, description: 'Score breakdown by category' },
    { id: 'bar' as ChartType, label: 'Benchmark Comparison', icon: BarChart3, description: 'Compare against industry standards' }
  ];

  const renderChart = () => {
    switch (activeChart) {
      case 'radar':
        return <ResultsChart results={results} />;
      case 'pie':
        return <ResultsPieChart results={results} />;
      case 'bar':
        return <ResultsBarChart results={results} />;
      default:
        return <ResultsChart results={results} />;
    }
  };

  return (
    <motion.div
      className="bg-white rounded-xl shadow-card overflow-hidden mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <div className="p-6 border-b border-gray-100">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Results Visualization</h3>
        
        <div className="flex flex-wrap gap-2">
          {chartTabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <motion.button
                key={tab.id}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeChart === tab.id
                    ? 'bg-primary-100 text-primary-700 border-2 border-primary-200'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-2 border-transparent'
                }`}
                onClick={() => setActiveChart(tab.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <IconComponent className="w-4 h-4" />
                <div className="text-left">
                  <div className="text-sm font-medium">{tab.label}</div>
                  <div className="text-xs opacity-75">{tab.description}</div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
      
      <div className="p-6">
        <Suspense fallback={
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        }>
          <motion.div
            key={activeChart}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {renderChart()}
          </motion.div>
        </Suspense>
      </div>
    </motion.div>
  );
};

export default ResultsVisualization;